import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MaterialModule } from '../../../../../shared/material.module';
import { LookupDataService } from '../../../../../services/lookupDataService.service';
import { OpbCompanyService } from '../../../../../services/opb-company.service';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { CountryFlat } from '../../../../../models/country';
import { Currency } from '../../../../../models/currency';
import { StockExchange } from '../../../../../models/stockExchange';
import { IndustryFlat } from '../../../../../models/industry';
import {
  OpbCompanyDto,
  OpbCompanyCreateRequest,
  OpbCompanyUpdateRequest,
  OpbCompanyInactivePeriodDto,
  OpbCompanyInactivePeriodCreateRequest
} from '../../../../../models/api/opbCompanyDto';
import { Helpers } from '../../../../../utilities/helpers';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';

@Component({
  selector: 'app-opb-details-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatChipsModule,
    NgxSpinnerModule
  ],
  templateUrl: './opb-details-tab.component.html',
  styleUrl: './opb-details-tab.component.scss'
})
export class OpbDetailsTabComponent implements OnInit {
  @Input() isEditMode = false;
  @Input() companyData: OpbCompanyDto | null = null;

  private fb = inject(FormBuilder);
  private lookupService = inject(LookupDataService);
  private opbCompanyService = inject(OpbCompanyService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private router = inject(Router);

  countries: CountryFlat[] = [];
  currencies: Currency[] = [];
  stockExchanges: StockExchange[] = [];
  allIndustries: IndustryFlat[] = [];

  // Active Measurements (inactive periods)
  measurements: string[] = Helpers.measurements();
  selectedMeasurements: string[] = [];
  private initialMeasurements: string[] = [];
  private existingInactivePeriods: OpbCompanyInactivePeriodDto[] = [];
  measurementsChanged = false;

  // CompanyBoardSystem is 1-based on the backend (Unitary = 1, Dual = 2). Do NOT copy the
  // company Details tab's 0-based BoardType values here, or every type renders off by one.
  boardTypeOptions = [
    { value: 1, label: 'Unitary' },
    { value: 2, label: 'Dual' }
  ];

  form: FormGroup = this.fb.group({
    name: ['', Validators.required],
    countryId: ['', Validators.required],
    stockExchangeId: ['', Validators.required],
    currencyId: ['', Validators.required],
    industryId: [''],
    superSectorId: [''],
    sectorId: [''],
    subSectorId: ['', Validators.required],
    boardSystem: [null],
    stockSymbol: [''],
    // Datastream Symbol is no longer shown in the form or list (mirrors company details), but
    // is kept in the model and round-tripped through patch/save so existing values are preserved.
    datastreamSymbol: [''],
    isin: [''],
    yearWentPublic: ['']
  });

  get industries(): IndustryFlat[] {
    return this.allIndustries.filter(i => !i.parentId);
  }

  get superSectors(): IndustryFlat[] {
    const industryId = this.form.get('industryId')?.value;
    if (!industryId) return [];
    return this.allIndustries.filter(i => i.parentId === industryId);
  }

  get sectors(): IndustryFlat[] {
    const superSectorId = this.form.get('superSectorId')?.value;
    if (!superSectorId) return [];
    return this.allIndustries.filter(i => i.parentId === superSectorId);
  }

  get subSectors(): IndustryFlat[] {
    const sectorId = this.form.get('sectorId')?.value;
    if (!sectorId) return [];
    return this.allIndustries.filter(i => i.parentId === sectorId);
  }

  get hasChanges(): boolean {
    return this.form.dirty || this.measurementsChanged;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  ngOnInit(): void {
    this.lookupService.getVisibleCountries<CountryFlat>().subscribe(data => {
      if (data) this.countries = data;
    });

    this.lookupService.getTableStream<Currency>('currencies').subscribe(data => {
      if (data) this.currencies = data;
    });

    this.lookupService.getTableStream<StockExchange>('stockExchanges').subscribe(data => {
      if (data) this.stockExchanges = data;
    });

    this.lookupService.getTableStream<IndustryFlat>('industries').subscribe(data => {
      if (data) {
        this.allIndustries = data;
        if (this.companyData) {
          this.patchFormWithIndustryHierarchy();
        }
      }
    });

    if (!this.isEditMode) {
      this.form.get('countryId')?.valueChanges.subscribe(countryId => {
        const country = this.countries.find(c => c.id === countryId);
        if (country?.currencyId) {
          this.form.get('currencyId')?.setValue(country.currencyId);
        }
      });
    }

    // Clear child selections when parent changes
    this.form.get('industryId')?.valueChanges.subscribe(() => {
      this.form.get('superSectorId')?.setValue('', { emitEvent: false });
      this.form.get('sectorId')?.setValue('', { emitEvent: false });
      this.form.get('subSectorId')?.setValue('', { emitEvent: false });
    });

    this.form.get('superSectorId')?.valueChanges.subscribe(() => {
      this.form.get('sectorId')?.setValue('', { emitEvent: false });
      this.form.get('subSectorId')?.setValue('', { emitEvent: false });
    });

    this.form.get('sectorId')?.valueChanges.subscribe(() => {
      this.form.get('subSectorId')?.setValue('', { emitEvent: false });
    });

    if (this.companyData) {
      this.patchForm(this.companyData);
      this.loadInactivePeriods();
    } else {
      // Create mode: select all measurements by default
      this.selectedMeasurements = [...this.measurements];
      this.initialMeasurements = [...this.measurements];
    }
  }

  onSave(): void {
    if (this.isEditMode && this.companyData) {
      const request = this.getUpdateRequest();
      if (!request) return;

      this.confirmationDialog.openSaveConfirmationDialog(
        'Are you sure you want to save these changes?'
      ).subscribe(confirmed => {
        if (!confirmed) return;

        this.spinnerService.show();
        this.opbCompanyService.updateOpbCompany(this.companyData!.id, request).subscribe({
          next: (company) => {
            this.companyData = company;

            if (this.measurementsChanged) {
              this.saveInactivePeriodsAsObservable(company.id).subscribe({
                next: () => {
                  this.form.markAsPristine();
                  this.spinnerService.hide();
                  this.toast.success('OPB Company updated successfully');
                },
                error: () => {
                  this.spinnerService.hide();
                  this.toast.error('Company updated but failed to save inactive periods');
                }
              });
            } else {
              this.form.markAsPristine();
              this.spinnerService.hide();
              this.toast.success('OPB Company updated successfully');
            }
          },
          error: () => {
            this.spinnerService.hide();
            this.toast.error('Failed to update OPB Company');
          }
        });
      });
    } else {
      // Create mode
      const request = this.getCreateRequest();
      if (!request) return;

      this.confirmationDialog.openCreateConfirmationDialog(
        'Are you sure you want to create this OPB company?'
      ).subscribe(confirmed => {
        if (!confirmed) return;

        this.spinnerService.show();
        this.opbCompanyService.createOpbCompany(request).subscribe({
          next: (company) => {
            if (this.measurementsChanged) {
              this.saveInactivePeriodsAsObservable(company.id).subscribe({
                next: () => {
                  this.spinnerService.hide();
                  this.toast.success('OPB Company created successfully');
                  this.form.markAsPristine();
                  this.measurementsChanged = false;
                  this.router.navigate(['/companies', 'opb-edit', company.id]);
                },
                error: () => {
                  this.spinnerService.hide();
                  this.toast.error('Company created but failed to save inactive periods');
                  this.router.navigate(['/companies', 'opb-edit', company.id]);
                }
              });
            } else {
              this.spinnerService.hide();
              this.toast.success('OPB Company created successfully');
              this.form.markAsPristine();
              this.measurementsChanged = false;
              this.router.navigate(['/companies', 'opb-edit', company.id]);
            }
          },
          error: () => {
            this.spinnerService.hide();
            this.toast.error('Failed to create OPB Company');
          }
        });
      });
    }
  }

  private patchForm(data: OpbCompanyDto): void {
    this.form.patchValue({
      name: data.name ?? '',
      countryId: data.countryId ?? '',
      stockExchangeId: data.stockExchangeId ?? '',
      currencyId: data.currencyId ?? '',
      boardSystem: data.boardSystem ?? null,
      stockSymbol: data.stockSymbol ?? '',
      datastreamSymbol: data.datastreamSymbol ?? '',
      isin: data.isin ?? '',
      yearWentPublic: Helpers.toDisplayDate(data.yearWentPublic)
    }, { emitEvent: false });

    this.form.markAsPristine();
  }

  private patchFormWithIndustryHierarchy(): void {
    if (!this.companyData?.sectorId || this.allIndustries.length === 0) return;

    const subSector = this.allIndustries.find(i => i.id === this.companyData!.sectorId);
    if (!subSector) return;

    const sector = subSector.parentId ? this.allIndustries.find(i => i.id === subSector.parentId) : null;
    const superSector = sector?.parentId ? this.allIndustries.find(i => i.id === sector.parentId) : null;
    const industry = superSector?.parentId ? this.allIndustries.find(i => i.id === superSector.parentId) : null;

    this.form.patchValue({
      industryId: industry?.id ?? '',
      superSectorId: superSector?.id ?? '',
      sectorId: sector?.id ?? '',
      subSectorId: subSector.id ?? ''
    }, { emitEvent: false });
  }

  // --- Inactive Periods / Active Measurements ---

  private loadInactivePeriods(): void {
    if (!this.companyData?.id) return;

    this.opbCompanyService.getInactivePeriods(this.companyData.id).subscribe({
      next: (periods) => {
        this.existingInactivePeriods = periods;
        const inactiveYears = new Set<string>();
        for (const period of periods) {
          const startYear = new Date(period.from).getFullYear();
          const endYear = new Date(period.until).getFullYear();
          for (let y = startYear; y < endYear; y++) {
            inactiveYears.add(String(y));
          }
        }
        this.selectedMeasurements = this.measurements.filter(m => !inactiveYears.has(m));
        this.initialMeasurements = [...this.selectedMeasurements];
        this.measurementsChanged = false;
      }
    });
  }

  onMeasurementsSelectionChange(event: any): void {
    this.selectedMeasurements = event.value;
    this.checkMeasurementsChanged();
  }

  selectAllMeasurements(): void {
    this.selectedMeasurements = [...this.measurements];
    this.checkMeasurementsChanged();
  }

  deselectAllMeasurements(): void {
    this.selectedMeasurements = [];
    this.checkMeasurementsChanged();
  }

  private checkMeasurementsChanged(): void {
    const current = [...this.selectedMeasurements].sort();
    const initial = [...this.initialMeasurements].sort();
    this.measurementsChanged = current.length !== initial.length ||
      current.some((v, i) => v !== initial[i]);
  }

  private buildInactivePeriods(): OpbCompanyInactivePeriodCreateRequest[] {
    const inactiveYears = this.measurements
      .filter(m => !this.selectedMeasurements.includes(m))
      .map(m => parseInt(m))
      .sort((a, b) => a - b);

    if (inactiveYears.length === 0) return [];

    const periods: OpbCompanyInactivePeriodCreateRequest[] = [];
    let start = inactiveYears[0];
    let prev = inactiveYears[0];

    for (let i = 1; i < inactiveYears.length; i++) {
      if (inactiveYears[i] === prev + 1) {
        prev = inactiveYears[i];
      } else {
        periods.push({
          from: new Date(`${start}-01-01T00:00:00Z`),
          until: new Date(`${prev + 1}-01-01T00:00:00Z`)
        });
        start = inactiveYears[i];
        prev = inactiveYears[i];
      }
    }
    periods.push({
      from: new Date(`${start}-01-01T00:00:00Z`),
      until: new Date(`${prev + 1}-01-01T00:00:00Z`)
    });

    return periods;
  }

  private saveInactivePeriodsAsObservable(companyId: string): Observable<any> {
    const deleteRequests = this.existingInactivePeriods.map(p =>
      this.opbCompanyService.deleteInactivePeriod(companyId, p.id)
    );

    const deleteAll$ = deleteRequests.length > 0 ? forkJoin(deleteRequests) : of([]);

    return deleteAll$.pipe(
      switchMap(() => {
        const newPeriods = this.buildInactivePeriods();
        if (newPeriods.length > 0) {
          const createRequests = newPeriods.map(p =>
            this.opbCompanyService.createInactivePeriod(companyId, p)
          );
          return forkJoin(createRequests).pipe(
            tap((created) => {
              this.existingInactivePeriods = created;
              this.initialMeasurements = [...this.selectedMeasurements];
              this.measurementsChanged = false;
            })
          );
        } else {
          this.existingInactivePeriods = [];
          this.initialMeasurements = [...this.selectedMeasurements];
          this.measurementsChanged = false;
          return of([]);
        }
      })
    );
  }

  // --- Request builders ---

  getCreateRequest(): OpbCompanyCreateRequest | null {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return null;
    }

    const v = this.form.getRawValue();
    return {
      name: v.name,
      countryId: v.countryId,
      stockExchangeId: v.stockExchangeId,
      currencyId: v.currencyId,
      sectorId: v.subSectorId,
      boardSystem: v.boardSystem ?? 1,
      stockSymbol: v.stockSymbol || '',
      datastreamSymbol: v.datastreamSymbol || '',
      isin: v.isin || '',
      yearWentPublic: Helpers.toIsoDate(v.yearWentPublic) || undefined
    };
  }

  getUpdateRequest(): OpbCompanyUpdateRequest | null {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return null;
    }

    const v = this.form.getRawValue();
    return {
      name: v.name,
      countryId: v.countryId,
      stockExchangeId: v.stockExchangeId,
      currencyId: v.currencyId,
      sectorId: v.subSectorId,
      boardSystem: v.boardSystem ?? 1,
      stockSymbol: v.stockSymbol || '',
      datastreamSymbol: v.datastreamSymbol || '',
      isin: v.isin || '',
      yearWentPublic: Helpers.toIsoDate(v.yearWentPublic) || undefined
    };
  }

  onDateFocus(event: Event): void {
    Helpers.onDateInputFocus(event);
  }

  onDateBlur(event: Event): void {
    Helpers.onDateInputBlur(event);
  }

  onYearDateInput(event: Event): void {
    const value = Helpers.onDateInputFormat(event);
    this.form.get('yearWentPublic')?.setValue(value, { emitEvent: false });
    this.form.get('yearWentPublic')?.markAsDirty();
  }
}
